import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faCheck, faComments, faFileLines, faLock, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import "./home.css";

const features = [
  {
    icon: faFileLines,
    title: "Sua história clínica como ponto de partida",
    description: "Cole os dados do prontuário uma única vez e mantenha o contexto do paciente organizado.",
  },
  {
    icon: faWandMagicSparkles,
    title: "Modelos da sua especialidade",
    description: "Tenha à mão suas evoluções, pareceres, sumários e documentos de rotina.",
  },
  {
    icon: faComments,
    title: "Vários documentos, uma só história",
    description: "Gere novas abas, revise o texto e adapte cada documento ao seu jeito de trabalhar.",
  },
];

const workflowSteps = [
  { number: "01", icon: faFileLines, title: "Cole os dados do paciente", description: "Comece com a história clínica, exames e informações relevantes do prontuário." },
  { number: "02", icon: faWandMagicSparkles, title: "Escolha um prompt ou modelo", description: "Use sua biblioteca para criar o documento certo para aquela situação clínica." },
  { number: "03", icon: faComments, title: "Revise e finalize", description: "O resultado abre em uma nova aba para você revisar, editar e copiar com segurança." },
];

export default function Home() {
  return (
    <div className="landing-page">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-brand" aria-label="HealthPad — início">
            <img src="/logo192.png" alt="" />
            <span>HealthPad</span>
          </Link>
          <nav className="landing-nav-actions" aria-label="Navegação principal">
            <a href="#como-funciona" className="landing-nav-link">Como funciona</a>
            <a href="#recursos" className="landing-nav-link">Recursos</a>
            <a href="#acesso" className="landing-nav-link">Solicite acesso</a>
            <Link to="/login" className="landing-login-link">Já tenho acesso · Entrar</Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <span className="landing-kicker"><span /> Para médicos que evoluem pacientes em enfermaria</span>
            <h1>Sua evolução clínica, mais clara. <em>Seu tempo, mais bem cuidado.</em></h1>
            <p className="landing-hero-description">
              O HealthPad reúne seus modelos de evolução e transforma os dados do prontuário em documentos prontos para a rotina de clínicos, especialistas interconsultores e cirurgiões.
            </p>
            <div className="landing-hero-actions">
              <a href="#acesso" className="landing-primary-button">
                Solicite acesso <FontAwesomeIcon icon={faArrowRight} />
              </a>
              <a href="#recursos" className="landing-secondary-link">Conhecer recursos</a>
            </div>
            <div className="landing-trust-note"><FontAwesomeIcon icon={faLock} /> Você decide, revisa e valida cada documento</div>
          </div>

          <div className="landing-hero-visual" aria-label="Prévia da plataforma HealthPad">
            <div className="landing-glow" />
            <div className="landing-product-window">
              <div className="product-window-bar">
                <div className="product-window-brand"><img src="/logo192.png" alt="" /> <strong>HealthPad</strong></div>
                <div className="product-window-dots"><span /><span /><span /></div>
              </div>
              <div className="product-window-body">
                <aside className="product-sidebar">
                  <span className="product-label">Biblioteca</span>
                  <div className="product-sidebar-item active"><i /> Prompts</div>
                  <div className="product-sidebar-item"><i /> Modelos</div>
                  <span className="product-label product-label-spaced">Edição</span>
                  <div className="product-sidebar-item"><i /> Copiar texto</div>
                  <div className="product-sidebar-item"><i /> Limpar documento</div>
                </aside>
                <div className="product-document">
                  <div className="product-tabs"><span className="selected">Aba 1</span><span>Aba 2</span><b>+</b></div>
                  <div className="product-paper">
                    <span className="paper-caption">Resumo clínico</span>
                    <h3>Informações do paciente</h3>
                    <div className="paper-line long" /><div className="paper-line" /><div className="paper-line medium" />
                    <span className="paper-caption paper-caption-gap">Plano de acompanhamento</span>
                    <div className="paper-line long" /><div className="paper-line medium" /><div className="paper-line short" />
                  </div>
                </div>
                <div className="product-assistant">
                  <div className="assistant-title"><span className="assistant-icon"><FontAwesomeIcon icon={faComments} /></span><strong>Assistente</strong><b>•••</b></div>
                  <div className="assistant-bubble">Como posso ajudar com este documento?</div>
                  <div className="assistant-bubble user-bubble">Revise o resumo clínico</div>
                  <div className="assistant-input">Digite uma mensagem...</div>
                </div>
              </div>
            </div>
            <div className="landing-float-card"><span className="float-check">✓</span><div><strong>Fluxo organizado</strong><small>Documentos em um só lugar</small></div></div>
          </div>
        </section>

        <section className="landing-intro-strip">
          <span>Para quem precisa evoluir bem — mesmo nos dias mais intensos</span>
          <div><b>CLÍNICOS</b><b>INTERCONSULTORES</b><b>CIRURGIÕES</b></div>
        </section>

        <section className="landing-workflow" id="como-funciona">
          <div className="landing-section-heading"><span className="landing-kicker">Do prontuário ao documento</span><h2>Menos tempo formatando.<br /><em>Mais tempo pensando no paciente.</em></h2><p>Um fluxo simples para transformar informação clínica em documentos úteis, com a sua biblioteca e o seu critério médico.</p></div>
          <div className="landing-workflow-grid">
            {workflowSteps.map((step) => <article className="landing-workflow-step" key={step.number}><div className="workflow-step-top"><span className="workflow-number">{step.number}</span><span className="workflow-icon"><FontAwesomeIcon icon={step.icon} /></span></div><h3>{step.title}</h3><p>{step.description}</p></article>)}
          </div>
        </section>

        <section className="landing-features" id="recursos">
          <div className="landing-section-heading"><span className="landing-kicker">Uma rotina mais leve</span><h2>Feito para a realidade da enfermaria.</h2><p>Tenha sua biblioteca de modelos à mão e transforme uma história clínica em documentos claros, sem perder o raciocínio médico.</p></div>
          <div className="landing-feature-grid">
            {features.map((feature) => <article className="landing-feature-card" key={feature.title}><div className="feature-icon"><FontAwesomeIcon icon={feature.icon} /></div><h3>{feature.title}</h3><p>{feature.description}</p><span className="feature-arrow"><FontAwesomeIcon icon={faArrowRight} /></span></article>)}
          </div>
        </section>

        <section className="landing-final-cta">
          <div><span className="landing-kicker">Comece pelo essencial</span><h2>Seu atendimento já é complexo.<br /><em>Sua ferramenta não precisa ser.</em></h2><p className="landing-cta-note">Solicite seu acesso e organize o próximo paciente em poucos passos.</p></div>
          <a href="#acesso" className="landing-primary-button">Solicite acesso <FontAwesomeIcon icon={faArrowRight} /></a>
        </section>

        <section className="landing-access" id="acesso">
          <div className="landing-access-copy"><span className="landing-kicker">Experimente na prática</span><h2>Sete dias para sentir a diferença na sua rotina.</h2><p>Tenha acesso gratuito e completo ao HealthPad para testar com o seu jeito de evoluir pacientes. Sem versão limitada durante o período de experiência.</p><p className="landing-openai-note"><FontAwesomeIcon icon={faWandMagicSparkles} /> Usamos modelos avançados da OpenAI para apoiar cada tarefa com mais qualidade e contexto.</p></div>
          <div className="landing-access-card"><span className="access-card-label">Acesso completo</span><div className="access-price"><strong>R$ 99</strong><span>/ mês</span></div><ul><li><FontAwesomeIcon icon={faCheck} /> 7 dias grátis</li><li><FontAwesomeIcon icon={faCheck} /> Biblioteca de prompts e modelos</li><li><FontAwesomeIcon icon={faCheck} /> Documentos em novas abas</li></ul><Link to="/login" className="landing-access-button">Solicitar acesso</Link><small>Já possui acesso? <Link to="/login">Entrar na plataforma</Link></small></div>
        </section>
      </main>

      <footer className="landing-footer"><Link to="/" className="landing-brand"><img src="/logo192.png" alt="" /><span>HealthPad</span></Link><span>Organização, confiança e cuidado.</span><small>© 2026 TBMed Serviços Médicos LTDA.</small></footer>
    </div>
  );
}
